import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Scissors, ChevronUp, ChevronDown, ChevronsUpDown, Loader2, Package } from "lucide-react";
import { toast } from "sonner";

function SortIcon({ field, sortField, sortDir }) {
  if (sortField !== field) return <ChevronsUpDown className="w-3 h-3 ml-1 opacity-40" />;
  return sortDir === "asc"
    ? <ChevronUp className="w-3 h-3 ml-1" />
    : <ChevronDown className="w-3 h-3 ml-1" />;
}

export default function CuttingRatesPage() {
  const { user } = useAuth();
  const [matrix, setMatrix] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchThickness, setSearchThickness] = useState("");
  const [searchMoq, setSearchMoq] = useState("");
  const [searchRate, setSearchRate] = useState("");

  const [sortField, setSortField] = useState("thickness");
  const [sortDir, setSortDir] = useState("asc");

  const [popupOpen, setPopupOpen] = useState(false);
  const [popupRow, setPopupRow] = useState(null);
  const [popupProducts, setPopupProducts] = useState([]);
  const [popupLoading, setPopupLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        // Use customer's own matrix if set; fall back to global matrix from settings
        let m = user?.cutting_cost_matrix || [];
        if (!m.length) {
          const res = await api.get("/settings/global");
          m = res.data.cutting_cost_matrix || [];
        }
        setMatrix(m);
      } catch {
        toast.error("Failed to load cutting rates");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const filtered = useMemo(() => {
    let rows = matrix.map((r, i) => ({ ...r, _idx: i }));

    if (searchThickness) {
      const q = searchThickness.toLowerCase();
      rows = rows.filter(r =>
        (r.thickness == null || r.thickness === "" ? "any" : String(r.thickness)).toLowerCase().includes(q)
      );
    }
    if (searchMoq) {
      rows = rows.filter(r => String(r.moq ?? 1).includes(searchMoq));
    }
    if (searchRate) {
      rows = rows.filter(r => String(r.rate ?? 0).includes(searchRate));
    }

    rows.sort((a, b) => {
      let av, bv;
      if (sortField === "thickness") {
        av = a.thickness == null || a.thickness === "" ? Infinity : parseFloat(a.thickness);
        bv = b.thickness == null || b.thickness === "" ? Infinity : parseFloat(b.thickness);
      } else if (sortField === "moq") {
        av = parseFloat(a.moq ?? 1);
        bv = parseFloat(b.moq ?? 1);
      } else {
        av = parseFloat(a.rate ?? 0);
        bv = parseFloat(b.rate ?? 0);
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });

    return rows;
  }, [matrix, searchThickness, searchMoq, searchRate, sortField, sortDir]);

  const openProductPopup = async (row) => {
    if (row.thickness == null || row.thickness === "" || parseFloat(row.thickness) === 0) {
      toast.info("No specific thickness — applicable to all products");
      return;
    }
    setPopupRow(row);
    setPopupOpen(true);
    setPopupLoading(true);
    setPopupProducts([]);
    try {
      const res = await api.get(`/products?thickness=${parseFloat(row.thickness)}&per_page=200`);
      const items = res.data.items || res.data;
      setPopupProducts(Array.isArray(items) ? items : []);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setPopupLoading(false);
    }
  };

  const SortHead = ({ field, children, className = "" }) => (
    <TableHead
      className={`bg-slate-900 text-white cursor-pointer select-none ${className}`}
      onClick={() => toggleSort(field)}
    >
      <span className="flex items-center">
        {children}
        <SortIcon field={field} sortField={sortField} sortDir={sortDir} />
      </span>
    </TableHead>
  );

  return (
    <div className="space-y-6 animate-slide-in" data-testid="cutting-rates-page">
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <Scissors className="w-6 h-6 text-orange-500" />
          <div>
            <h1 className="font-heading text-2xl uppercase tracking-wider">Cutting Rate Matrix</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Your applicable cutting rates by material thickness and order quantity. Click a row to view matching products.
            </p>
          </div>
        </div>
      </div>

      <Card className="card-industrial overflow-hidden">
        <Table>
          <TableHeader>
            {/* Sort headers */}
            <TableRow>
              <SortHead field="thickness">Thickness (mm)</SortHead>
              <SortHead field="moq" className="text-right">Min. Qty (MOQ)</SortHead>
              <SortHead field="rate" className="text-right">Rate (₹ / pcs)</SortHead>
            </TableRow>
            {/* Search row */}
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="py-1.5 px-3">
                <Input
                  value={searchThickness}
                  onChange={e => setSearchThickness(e.target.value)}
                  placeholder="Search thickness..."
                  className="h-7 rounded-none text-xs font-mono border-slate-300"
                />
              </TableHead>
              <TableHead className="py-1.5 px-3">
                <Input
                  value={searchMoq}
                  onChange={e => setSearchMoq(e.target.value)}
                  placeholder="Search MOQ..."
                  className="h-7 rounded-none text-xs font-mono border-slate-300"
                />
              </TableHead>
              <TableHead className="py-1.5 px-3">
                <Input
                  value={searchRate}
                  onChange={e => setSearchRate(e.target.value)}
                  placeholder="Search rate..."
                  className="h-7 rounded-none text-xs font-mono border-slate-300"
                />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-12 text-slate-400">
                  <Scissors className="w-10 h-10 mx-auto mb-3 opacity-20" />
                  <p className="text-sm">No cutting rate entries found</p>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((row, i) => {
                const isWildcard = row.thickness == null || row.thickness === "" || parseFloat(row.thickness) === 0;
                return (
                  <TableRow
                    key={i}
                    className="cursor-pointer hover:bg-orange-50 transition-colors"
                    onClick={() => openProductPopup(row)}
                    title={isWildcard ? "Applies to any thickness" : "Click to view matching products"}
                  >
                    <TableCell className="font-mono font-medium">
                      {isWildcard
                        ? <span className="text-slate-400 italic text-xs">Any thickness</span>
                        : <>{parseFloat(row.thickness).toFixed(2)} mm</>}
                    </TableCell>
                    <TableCell className="font-mono text-right">{(row.moq ?? 1).toLocaleString()} pcs</TableCell>
                    <TableCell className="font-mono font-semibold text-right text-orange-700">
                      ₹{parseFloat(row.rate ?? 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {!loading && filtered.length > 0 && (
          <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
            {filtered.length} {filtered.length === 1 ? "entry" : "entries"}
            {(searchThickness || searchMoq || searchRate) && matrix.length !== filtered.length && ` (filtered from ${matrix.length})`}
          </div>
        )}
      </Card>

      {/* Products popup */}
      <Dialog open={popupOpen} onOpenChange={setPopupOpen}>
        <DialogContent className="w-full max-w-2xl h-[100dvh] md:h-auto md:max-h-[80vh] overflow-y-auto rounded-none p-0 md:p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg uppercase tracking-wider flex items-center gap-2">
              <Package size={18} />
              Products — {popupRow && parseFloat(popupRow.thickness).toFixed(2)} mm
            </DialogTitle>
          </DialogHeader>

          {/* Cutting rate summary */}
          {popupRow && (
            <div className="flex gap-3 mt-2 mb-1">
              <div className="bg-orange-50 border border-orange-200 px-4 py-2 text-sm flex-1">
                <span className="text-orange-600 text-xs uppercase tracking-wider block mb-0.5">Cutting Rate</span>
                <span className="font-mono font-bold text-orange-700">₹{parseFloat(popupRow.rate ?? 0).toFixed(2)} / pcs</span>
                <span className="text-slate-400 text-xs ml-2">(MOQ {(popupRow.moq ?? 1).toLocaleString()} pcs)</span>
              </div>
            </div>
          )}

          {popupLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-7 h-7 animate-spin text-slate-400" />
            </div>
          ) : popupProducts.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <Package className="w-10 h-10 mx-auto mb-3 opacity-20" />
              <p className="text-sm">No active products found for this thickness</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="bg-slate-900 text-white">Product</TableHead>
                  <TableHead className="bg-slate-900 text-white">Width</TableHead>
                  <TableHead className="bg-slate-900 text-white text-right">Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {popupProducts.map((p, i) => {
                  const desc = [p.product_name, p.list_1, p.list_2].filter(Boolean).join(" · ");
                  const rate = p.display_rate ?? p.erp_rate ?? 0;
                  const uom = (p.applied_rate_uom || "").toUpperCase();
                  return (
                    <TableRow key={i}>
                      <TableCell>
                        <p className="font-medium text-sm">{desc}</p>
                        <p className="text-xs text-slate-400 font-mono">{p.product_code}</p>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {p.width ? `${Number(p.width).toLocaleString()} mm` : "—"}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold text-sm">
                        ₹{parseFloat(rate).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {uom && <span className="text-slate-400 font-normal text-xs ml-1">/{uom === "SQM" ? "sq.m" : uom === "MTR" ? "mtr" : uom.toLowerCase()}</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
